import { useEffect, useState } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { map, flatMap, filter, sumBy, pickBy, forEach } from 'lodash'
import { useForm } from 'react-hook-form'
import { numberToCurrency } from 'lib/money'
import { toDate, format } from 'date-fns'
import { Checkbox } from 'components/forms'
import { InfoButton, LinkButton } from 'components/buttons'
import { errorMessage, successMessage } from 'lib/flash-message'
import { redeemGroupGifts, getUserCredit } from 'registry/api'
import css from './group-gift-funds-tab-content.scss'

const GroupGiftFundsTabContent = ({
  creditBalance,
  groupGiftTotalFundedAmount,
  groupGifts,
  isOwner,
  setUserCredit,
}) => {
  const { register, getValues, setValue } = useForm()
  const [redeemableGroupGiftsFunds, setRedeemableGroupGiftsFunds] = useState([])
  const [totalUnredeemedFunds, setTotalUnredeemedFunds] = useState(0)
  const [totalFundsToRedeem, setTotalFundsToRedeem] = useState(0)
  const [someChecked, setSomeChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const creditBalanceAfterRedemption = creditBalance + totalFundsToRedeem

  useEffect(() => {
    // Pull out all the unredeemed funds for every group gift
    const allUnredeemedFunds = flatMap(
      filter(groupGifts, ({ unredeemedFunds }) => unredeemedFunds.length > 0),
      ({ unredeemedFunds }) => unredeemedFunds
    )
    const totalUnredeemedFundsAmount = sumBy(
      allUnredeemedFunds,
      (f) => f.amount
    )

    setRedeemableGroupGiftsFunds(allUnredeemedFunds)
    setTotalUnredeemedFunds(totalUnredeemedFundsAmount)
  }, [groupGifts])

  const titleDisplayText = ({ title, isActive, isRegItemActive }) => {
    if (!isRegItemActive) {
      return `${title} (Deleted)`
    }
    if (!isActive) {
      return `${title} (Disabled)`
    }
    return title
  }

  const getCheckedFundIds = () => {
    return Object.keys(
      pickBy(getValues(), (checked, fundId) => {
        if (checked) {
          return fundId
        }
        return false
      })
    )
  }

  const handleOnChange = () => {
    const checkedFundIds = getCheckedFundIds()
    setSomeChecked(checkedFundIds.length > 0)
    const selectedFunds = filter(redeemableGroupGiftsFunds, ({ id }) =>
      checkedFundIds.includes(id.toString())
    )
    const totalSelectedFundsAmount = sumBy(selectedFunds, (f) => f.amount)
    setTotalFundsToRedeem(totalSelectedFundsAmount)
  }

  const handleCheckAll = () => {
    forEach(getValues(), (checked, fundId) => {
      setValue(fundId, true)
    })
    handleOnChange()
  }

  const resetState = () => {
    setSubmitting(false)
    setTotalFundsToRedeem(0)
    setSomeChecked(false)
  }

  const handleApplyFunds = () => {
    setSubmitting(true)
    const fundIdsToRedeem = getCheckedFundIds()
    redeemGroupGifts({ fundIds: fundIdsToRedeem })
      .then(() => {
        // re-fetch user credit api
        getUserCredit()
          .then((resp) => {
            if (resp.userCredit.isOwner) {
              successMessage(
                'Group gift funds have been applied to your account.'
              )
            } else {
              successMessage(
                "Group gift funds have been applied to the registry owner's account."
              )
            }
            setUserCredit(resp)
          })
          .catch(() => {
            errorMessage()
          })
      })
      .catch(() => {
        errorMessage()
      })
      .then(() => {
        resetState()
      })
  }

  return (
    <>
      <div className="mtl mbxl h6">
        {redeemableGroupGiftsFunds.length > 0 ? (
          <>
            <strong>
              Select the gift funds you want applied to your next Babylist Shop
              checkout.
            </strong>{' '}
            Applied funds will be converted to gift card credit
            {creditBalance > 0 && (
              <>
                {' '}
                and combined with your existing{' '}
                <span className="text-credit">
                  {numberToCurrency(creditBalance, { truncateZeroCents: true })}
                </span>{' '}
                gift card credit
              </>
            )}
          </>
        ) : (
          <div className="text-center">
            <em>
              There are no group gift funds to apply to{' '}
              {isOwner ? 'your account' : "the registry owner's account"}.
            </em>
          </div>
        )}
      </div>

      <form>
        {totalUnredeemedFunds > 0 && (
          <div className="mbl">
            <LinkButton className="pan man flex grow" onClick={handleCheckAll}>
              Select All Available Funds (
              <span className="text-bold">
                {numberToCurrency(totalUnredeemedFunds, {
                  truncateZeroCents: true,
                })}
              </span>
              )
            </LinkButton>
          </div>
        )}

        {map(
          groupGifts,
          ({
            fundedAmount,
            fundsRedeemedAmount,
            isActive,
            isRegItemActive,
            regItemId,
            title,
            unredeemedFunds,
          }) => {
            return (
              <div key={regItemId} className="mvm">
                <hr className="mbm" />
                <div>
                  <span className="text-muted text-uppercase text-bold tiny lh-sm">
                    {titleDisplayText({ title, isActive, isRegItemActive })}
                  </span>

                  <div className="mvl">
                    {fundsRedeemedAmount > 0 && (
                      <p className="h6 mvl">
                        <em>
                          {numberToCurrency(fundsRedeemedAmount)} of the{' '}
                          {numberToCurrency(fundedAmount)} funded for this item
                          has already been applied.
                        </em>
                      </p>
                    )}
                    {unredeemedFunds && unredeemedFunds.length > 0 && (
                      <div className={classNames(css.fundDisplay, 'mtl')}>
                        {unredeemedFunds.map(
                          ({ amount, fundedAt, funderName, id }) => {
                            const date = toDate(new Date(fundedAt))
                            return (
                              <Checkbox
                                groupClassName={css.fund}
                                defaultChecked={false}
                                key={id}
                                name={id.toString()}
                                inputRef={register}
                                label={
                                  <div className="h6 man text-bold">{`$${amount} from ${funderName}`}</div>
                                }
                                onChange={handleOnChange}
                                help={
                                  <div className="xsmall man">{`Funded on ${format(
                                    date,
                                    'M/dd/yyyy'
                                  )}`}</div>
                                }
                              />
                            )
                          }
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          }
        )}
      </form>
      {someChecked && (
        <div className={classNames(css.fixedBottomBar, 'animated nudgeInUp')}>
          <div className="text-center">
            <InfoButton
              onClick={handleApplyFunds}
              pill
              disabled={!someChecked}
              submitting={submitting}
            >
              Apply Funds at Checkout
            </InfoButton>
          </div>
          {creditBalanceAfterRedemption > 0 && (
            <div className="small mtm phs text-bold text-center">
              You'll have {numberToCurrency(creditBalanceAfterRedemption)} in
              total gift card credit at checkout.
            </div>
          )}
        </div>
      )}
    </>
  )
}

GroupGiftFundsTabContent.propTypes = {
  creditBalance: PropTypes.number.isRequired,
  groupGiftTotalFundedAmount: PropTypes.number.isRequired,
  groupGifts: PropTypes.arrayOf(
    PropTypes.shape({
      regItemId: PropTypes.number.isRequired,
      fundsRedeemedAmount: PropTypes.number.isRequired,
      fundedAmount: PropTypes.number.isRequired,
      isActive: PropTypes.bool.isRequired,
      isRegItemActive: PropTypes.bool.isRequired,
      title: PropTypes.string.isRequired,
      userCreditBalance: PropTypes.number.isRequired,
      unredeemedFunds: PropTypes.array.isRequired,
    })
  ).isRequired,
  isOwner: PropTypes.bool.isRequired,
  setUserCredit: PropTypes.func.isRequired,
}

export default GroupGiftFundsTabContent
